import { z } from 'zod';

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------
//
// Business types accepted on the LOGIN form. OTHERS is the platform-level
// "Ardinotech" admin tenant — it is intentionally exposed on login (so the
// platform admin can sign in) but NOT on registration.
export const loginSchema = z.object({
  usernameOrPhone: z
    .string()
    .min(1, 'Username or phone number is required')
    .max(100),
  password: z.string().min(1, 'Password is required').max(64),
  businessType: z.enum(
    ['WHOLESALER', 'RETAILER', 'REPRESENTATIVE', 'OTHERS'],
    { errorMap: () => ({ message: 'Select a business type' }) }
  ),
});

export type LoginFormValues = z.infer<typeof loginSchema>;

// ---------------------------------------------------------------------------
// Tenant registration
// Mirrors backend TenantRegistrationRequest.java
// ---------------------------------------------------------------------------
const optionalString = z.string().max(200).optional().or(z.literal(''));
const optionalEmail = z
  .string()
  .email('Invalid email')
  .optional()
  .or(z.literal(''));

export const tenantRegistrationSchema = z.object({
  // Business
  tenantName: z.string().min(1, 'Business name is required').max(100),
  tenantType: z.enum(['WHOLESALER', 'RETAILER', 'REPRESENTATIVE'], {
    errorMap: () => ({ message: 'Select a business type' }),
  }),
  industry: z.enum(
    [
      'PHARMACEUTICAL',
      'FMCG',
      'ELECTRONICS',
      'APPAREL',
      'FOOD_BEVERAGE',
      'HARDWARE',
      'STATIONERY',
      'AUTOMOTIVE',
      'OTHER',
    ],
    { errorMap: () => ({ message: 'Select an industry' }) }
  ),
  gstin: z.string().max(20).optional().or(z.literal('')),

  // Regulatory licences — all optional. Pharmaceutical tenants typically
  // supply Drug License; food/F&B tenants the FSSAI (Food) license.
  // The certificate file itself is uploaded separately after registration
  // succeeds (see RegisterTenantPage).
  drugLicenseNumber: z.string().max(50).optional().or(z.literal('')),
  drugLicenseExpiry: z.string().optional().or(z.literal('')), // YYYY-MM-DD
  foodLicenseNumber: z.string().max(50).optional().or(z.literal('')),
  foodLicenseExpiry: z.string().optional().or(z.literal('')), // YYYY-MM-DD

  // Login credentials chosen by the user at registration time. The backend
  // applies sensible defaults if these are left blank, but the UI now asks
  // for them explicitly (right after GSTIN).
  adminUsername: z
    .string()
    .min(3, 'Username must be at least 3 characters')
    .max(100, 'Username too long')
    // Letters, digits, dot / underscore / hyphen — same character set the
    // backend treats as a clean username.
    .regex(/^[A-Za-z0-9._-]+$/, 'Use letters, digits, . _ or - only'),
  adminPassword: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(64, 'Password too long'),

  // Address
  address: optionalString,
  area: optionalString,
  city: optionalString,
  district: optionalString,
  state: optionalString,
  pincode: z.string().max(10).optional().or(z.literal('')),
  phone: z
    .string()
    .min(7, 'Enter a valid phone number')
    .max(20, 'Phone number too long'),
  tenantEmail: optionalEmail,
  /**
   * Accounting / ERP software the tenant uses.
   * Maps to the vendor_name column. Default "NONE".
   */
  vendorName: z.string().max(50).optional().or(z.literal('')),

  /** Required consent checkbox — must be checked to submit the form. */
  agreedToTerms: z
    .boolean()
    .refine((v) => v === true, { message: 'You must agree to the Terms & Conditions and Privacy Policy to continue' }),
});

export type TenantRegistrationFormValues = z.infer<typeof tenantRegistrationSchema>;

// ---------------------------------------------------------------------------
// Product
// Mirrors backend ProductDto.java. minOrderQty is intentionally NOT on the
// form anymore — the column lingers in the DB for backward-compat but the
// wholesaler workflow uses PTR/PTS/Available/Scheme instead.
// ---------------------------------------------------------------------------
export const productSchema = z.object({
  sku: z.string().min(1, 'SKU required').max(50),
  name: z.string().min(1, 'Name required').max(200),
  description: z.string().max(1000).optional().or(z.literal('')),
  brand: z.string().max(100).optional().or(z.literal('')),
  genericName: z.string().max(200).optional().or(z.literal('')),
  packSize: z.string().max(50).optional().or(z.literal('')),
  uom: z.string().min(1, 'UOM required').max(20),
  hsnCode: z.string().max(20).optional().or(z.literal('')),
  gstRate: z.coerce.number().min(0).max(100).optional(),
  mrp: z.coerce.number().min(0).optional(),
  ptr: z.coerce.number().min(0, 'PTR cannot be negative').optional(),
  pts: z.coerce.number().min(0, 'PTS cannot be negative').optional(),
  availableQty: z.coerce.number().int().min(0).optional(),
  discountPct: z.coerce.number().min(0).max(100).optional(),
  // Scheme is "buy:free" — both sides plain digits. Empty string clears it.
  // Validation message mirrors the backend @Pattern on ProductDto.
  scheme: z
    .string()
    .max(20)
    .regex(/^\d+:\d+$/, 'Scheme must be in qty:free format, e.g. 10:1')
    .optional()
    .or(z.literal('')),
  // Near-expiry month, "mm-yyyy" e.g. "08-2026". Mirrors backend @Pattern on ProductDto.
  nearExpiry: z
    .string()
    .regex(/^(0[1-9]|1[0-2])-\d{4}$/, 'Near expiry must be in mm-yyyy format, e.g. 08-2026')
    .optional()
    .or(z.literal('')),
});

export type ProductFormValues = z.infer<typeof productSchema>;

// ---------------------------------------------------------------------------
// Party (B2B customer book)
// Mirrors backend PartyDto.java — every field on this schema must have a
// matching @Size / @NotBlank on the server, and vice-versa.
// ---------------------------------------------------------------------------
export const partySchema = z.object({
  name: z.string().min(1, 'Party name is required').max(200),
  /**
   * Business type of the party (chip on the parties list). Optional;
   * defaults to nothing on the server. The form picks RETAILER as the
   * sensible default since it's the most common case for a wholesaler.
   */
  partyType: z
    .enum(['RETAILER', 'WHOLESALER', 'DISTRIBUTOR', 'OTHERS'])
    .optional(),
  /** Default pricing tier billed to this party. Server defaults to PTR. */
  defaultPricing: z.enum(['PTR', 'PTS']).optional(),
  partyCode: z.string().min(1, 'Party code is required').max(50),
  partyExternalId: z.string().max(100).optional().or(z.literal('')),
  contactPerson: z.string().max(150).optional().or(z.literal('')),
  mobile: z.string().max(20).optional().or(z.literal('')),
  phone: z.string().max(20).optional().or(z.literal('')),
  email: z
    .string()
    .max(150)
    .email('Invalid email')
    .optional()
    .or(z.literal('')),
  gstin: z.string().max(20).optional().or(z.literal('')),
  panNumber: z.string().max(20).optional().or(z.literal('')),
  dlNumber: z.string().max(50).optional().or(z.literal('')),
  // YYYY-MM-DD from <input type="date" />, or empty string
  dlExpiry: z.string().optional().or(z.literal('')),
  repName: z.string().max(100).optional().or(z.literal('')),
  addressLine1: z.string().max(200).optional().or(z.literal('')),
  addressLine2: z.string().max(200).optional().or(z.literal('')),
  area: z.string().max(100).optional().or(z.literal('')),
  city: z.string().max(100).optional().or(z.literal('')),
  pincode: z.string().max(10).optional().or(z.literal('')),
});

export type PartyFormValues = z.infer<typeof partySchema>;
export type PartyType = NonNullable<PartyFormValues['partyType']>;
export type PartyPricingTier = NonNullable<PartyFormValues['defaultPricing']>;

// ---------------------------------------------------------------------------
// Tenant Party ("My Parties")
// Mirrors backend TenantPartyDto.java. Used by the new "My Parties" tab on
// the parties page — tenant-owned party records that can be mapped to
// All Parties rows (auto by GSTIN, or manually via the autocomplete on
// the All Parties tab).
//
// Field set mirrors the Create Party form: identity (name, code, party id,
// type), address (line1/2, area, city, pincode), contact (person, mobile,
// phone, email), and tax/licence (gstin, pan, DL number, DL expiry, rep
// name).
//
// DL Number is industry-conditional: pharmaceutical tenants MUST supply
// it. Use {@link makeTenantPartySchema} from the form to apply the rule;
// the bare {@link tenantPartySchema} keeps DL Number optional for callers
// that don't have an industry context (e.g. shared list rendering).
// ---------------------------------------------------------------------------
export const tenantPartySchema = z.object({
  // Identity
  name: z.string().min(1, 'Party name is required').max(200),
  partyCode: z.string().max(50).optional().or(z.literal('')),
  partyExternalId: z.string().max(100).optional().or(z.literal('')),
  /** Same enum as Party.partyType — drives the chip on the My Party cell. */
  partyType: z
    .enum(['RETAILER', 'WHOLESALER', 'DISTRIBUTOR', 'OTHERS'])
    .optional(),
  /** Default pricing tier billed to this party. */
  defaultPricing: z.enum(['PTR', 'PTS']).optional(),

  // Contact
  contactPerson: z.string().max(150).optional().or(z.literal('')),
  mobile: z.string().max(20).optional().or(z.literal('')),
  phone: z.string().max(20).optional().or(z.literal('')),
  email: z
    .string()
    .max(150)
    .email('Invalid email')
    .optional()
    .or(z.literal('')),

  // Tax / licence
  gstin: z.string().max(20).optional().or(z.literal('')),
  panNumber: z.string().max(20).optional().or(z.literal('')),
  dlNumber: z.string().max(50).optional().or(z.literal('')),
  // YYYY-MM-DD from <input type="date" /> or empty
  dlExpiry: z.string().optional().or(z.literal('')),

  // Sales rep
  repName: z.string().max(100).optional().or(z.literal('')),

  // Address
  addressLine1: z.string().max(200).optional().or(z.literal('')),
  addressLine2: z.string().max(200).optional().or(z.literal('')),
  area: z.string().max(100).optional().or(z.literal('')),
  city: z.string().max(100).optional().or(z.literal('')),
  pincode: z.string().max(10).optional().or(z.literal('')),
});

export type TenantPartyFormValues = z.infer<typeof tenantPartySchema>;

/**
 * Industry-aware variant of {@link tenantPartySchema}. Pass the tenant's
 * industry (from `useAuthStore().user?.industry`); when it equals
 * `PHARMACEUTICAL` the DL Number field becomes required. All other rules
 * are inherited from the base schema.
 */
export function makeTenantPartySchema(industry?: string | null) {
  if (industry === 'PHARMACEUTICAL') {
    return tenantPartySchema.extend({
      dlNumber: z
        .string()
        .min(1, 'DL Number is required for pharmaceutical tenants')
        .max(50),
    });
  }
  return tenantPartySchema;
}
